import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Autocomplete } from "@/components/ui/autocomplete";
import { useFocusServiceListSearch, type ServiceListOption } from "@/hooks/useFocusServiceListSearch";

interface FocusServiceListAutocompleteProps {
  municipalityCode: string;
  value?: string | null;
  onChange: (value: string, taxRate: number | null) => void;
  placeholder: string;
  disabled?: boolean;
}

export function FocusServiceListAutocomplete({
  municipalityCode,
  value,
  onChange,
  placeholder,
  disabled,
}: FocusServiceListAutocompleteProps) {
  const { t } = useTranslation("erp");
  const [displayText, setDisplayText] = useState(value ?? "");
  const normalizedValue = value ?? "";

  // Exact lookup to resolve label for an existing saved code value
  const exactQuery = useFocusServiceListSearch({
    municipalityCode,
    query: normalizedValue,
    enabled: normalizedValue.length >= 2,
  });

  const searchQuery = useFocusServiceListSearch({
    municipalityCode,
    query: displayText,
    enabled: !disabled,
  });

  const options = useMemo(
    () =>
      (searchQuery.data?.options ?? []).map((o: ServiceListOption) => ({
        value: o.value,
        label: o.label,
      })),
    [searchQuery.data],
  );

  // When the external value changes, resolve its display label
  useEffect(() => {
    if (!normalizedValue) {
      setDisplayText("");
      return;
    }
    const match = exactQuery.data?.options?.find((o) => o.value === normalizedValue);
    if (match) {
      setDisplayText(match.label);
    } else if (!exactQuery.data) {
      // Data not loaded yet — show the raw code as placeholder so the field isn't blank
      setDisplayText(normalizedValue);
    }
  }, [normalizedValue, exactQuery.data]);

  const isLoading = searchQuery.isFetching;

  return (
    <div className="relative">
      <Autocomplete
        options={options}
        value={displayText}
        selectedValue={normalizedValue}
        onChange={(text) => {
          setDisplayText(text);
          if (!text.trim()) onChange("", null);
        }}
        onSelect={(option) => {
          const matched = searchQuery.data?.options?.find((o) => o.value === option.value);
          const taxRate = matched?.taxRate ?? null;
          setDisplayText(option.label);
          onChange(option.value, taxRate);
        }}
        placeholder={placeholder}
        disabled={disabled}
        disableLocalFilter
        emptyMessage={
          isLoading
            ? t("common.loading")
            : displayText.trim().length < 2
            ? t("itemForm.fiscal.nfse.serviceList.typeToSearch")
            : t("itemForm.fiscal.nfse.serviceList.noResults")
        }
      />
      {isLoading && (
        <Loader2 className="pointer-events-none absolute right-8 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}
